import type { ContentStatus, OrgContentSource } from "@/types/api";

/**
 * What a document's status means to the person who uploaded it.
 *
 * The four working states are deliberately distinct rather than collapsed into
 * "processing": an admin watching a 200-page PDF wants to know it has got past
 * extraction, and "stuck at pending" is a different problem from "stuck at
 * embedding" — the first means the ingest trigger never landed, the second
 * means the embedding provider is unhappy.
 */
export function describeStatus(status: ContentStatus): {
	label: string;
	tone: "active" | "invited" | "critical" | "inactive" | "neutral";
	hint?: string;
} {
	switch (status) {
		case "ready":
			return { label: "Ready", tone: "active" };
		case "pending":
			return {
				label: "Queued",
				tone: "invited",
				hint: "Waiting to start.",
			};
		case "extracting":
			return {
				label: "Reading",
				tone: "invited",
				hint: "Pulling the text out of your document.",
			};
		case "embedding":
			return {
				label: "Indexing",
				tone: "invited",
				hint: "Making it searchable for your team.",
			};
		case "failed":
			return { label: "Failed", tone: "critical" };
		case "removed":
			return { label: "Removed", tone: "inactive" };
	}
}

export function isBusy(status: ContentStatus): boolean {
	return (
		status === "pending" || status === "extracting" || status === "embedding"
	);
}

/** Bytes for humans. Returns null when there is no size to show. */
export function formatBytes(bytes: number | null): string | null {
	if (bytes == null || bytes <= 0) return null;
	const units = ["B", "KB", "MB", "GB"];
	let n = bytes;
	let i = 0;
	while (n >= 1024 && i < units.length - 1) {
		n /= 1024;
		i++;
	}
	return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

/**
 * What this document IS, in one phrase.
 *
 * A URL's own address is more use than the word "Link" — it is how an admin
 * recognises which of four crawled pages this row is.
 */
export function describeSource(s: OrgContentSource): string {
	if (s.kind === "file") return s.file_name ?? "File";
	if (s.kind === "url") return s.source_url ?? "Link";
	return "Pasted text";
}

/**
 * How full the library is, and whether that is worth saying out loud.
 *
 * Silent until it matters: a bar that is always shouting gets ignored, and the
 * number an admin actually needs is "can I add the next document", not a
 * running percentage.
 */
export function quotaState(
	used: number,
	quota: number,
): {
	pct: number;
	tone: "ok" | "warn" | "full";
} {
	const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
	if (used >= quota) return { pct: 100, tone: "full" };
	return { pct, tone: pct >= 80 ? "warn" : "ok" };
}
